import { ReactNode } from 'react';
import styled from 'styled-components';

import BubbleAsk from '@/assets/lottie/bubble-ask.json';
import BreadCrumbs from '@/components/atom/BreadCrumbs';
import CustomLottieIcon from '@/components/atom/CustomLottieIcon';
import StickyButton from '@/components/atom/buttons/StickyButton';
import CategoryBottomSheet from '@/components/common/communication/qna/CategoryBottomSheet';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import InfoBox, { INFO_BOX_VARIANT } from '@/components/molecules/InfoBox';
import StickyAppHeader from '@/components/molecules/StickyAppHeader';
import PolicyAgreementBottomSheet from '@/components/templates/mobile/communication/Qna/QnaWriteMobileTemplate/PolicyAgreementBottomSheet';
import { QnaWriteMobileViewModel } from '@/hooks/qna/useQnaWriteMobileViewModel';
import { ClientType, CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';

import QnaWriteCategory from './QnaWriteCategory';
import QnaWriteContentTextarea from './QnaWriteContentTextarea';
import QnaWriteImages from './QnaWriteImages';
import WriteOnBoarding from './WriteOnBoarding';

const infoBoxMessage: Array<ReactNode> = [
  '폭력적/공격적인 글, 서비스 목적과 맞지 않는 질문, 3회 이상 중복된(도배성) 질문은 예고 없이 삭제될 수 있어요.',
  '서비스 운영을 방해하는 행위가 반복적으로 발생할 경우, 회원 및 의료진 보호를 위해 서비스 이용이 한시적 또는 영구적으로 사전 안내 없이 제한될 수 있어요.',
  '실시간 의료상담 서비스에서 받은 질문의 내용과 답변은 모두에게 공개되며, 서비스 홍보의 목적으로 활용될 수 있어요.',
  '답변은 의학적 판단이나 진료 행위로 해석될 수 없으며, 모든 약물 상호작용을 포함하지 않아요. 닥터나우와 답변 의료인은 이로 인해 발생하는 어떠한 책임도 지지 않아요.',
  '정확한 진단을 위해서는 닥터나우 앱 내에서 원격진료를 신청하거나, 병원을 방문해 주세요.',
  '실시간 의료상담에 등록하신 질문은 고객센터를 통한 삭제 요청이 없는 한 지속적으로 남게 돼요.',
  '의료진 판단 하에 비대면으로 답변이 어려운 질문은 환불이 진행될 수 있어요.',
];

interface Props extends CustomPageProps {
  qnaWriteMobileViewModel: QnaWriteMobileViewModel;
}

const QnaWriteMobileTemplate = ({ client, qnaWriteMobileViewModel: { ...viewModel } }: Props) => {
  return (
    <TemplateLayout.Wrapper>
      {client.isWebView && (
        <TemplateLayout.Header useSafeArea>
          <StickyAppHeader title="" />
        </TemplateLayout.Header>
      )}
      <TemplateLayout.Content>
        {client.clientType === ClientType.WEB && (
          <BreadCrumbs.Wrapper>
            <BreadCrumbs visible path={`${ROUTES.QNA.WRITE.path}`} type="qna" />
          </BreadCrumbs.Wrapper>
        )}
        <QuickAnswerWriteTemplateContainer>
          <TopTitleWrapper>
            <CustomLottieIcon width={40} height={40} animationData={BubbleAsk} />
            <TopTitle>건강고민을 물어보세요</TopTitle>
            <TopDescription>평균 5분 안에 답변 드려요</TopDescription>
          </TopTitleWrapper>
          <QuickAnswerWriteFormWrapper>
            <QnaWriteContentTextarea
              question={viewModel.question}
              onChangeQuestion={viewModel.setQuestion}
              placeholder={viewModel.selectedCategoriesBottomSheetTabItem?.contentPlaceholder}
            />

            <QnaWriteCategory
              selectedTags={viewModel.selectedTags}
              onChangeSelectedTags={viewModel.setSelectedTags}
              selectedCategoriesBottomSheetTabItem={viewModel.selectedCategoriesBottomSheetTabItem}
              showCategoriesBottomSheet={viewModel.showCategoriesBottomSheet}
              toggleCategoriesBottomSheet={viewModel.toggleCategoriesBottomSheet}
              onClickCheckPregnant={viewModel.onClickCheckPregnant}
              pregnant={viewModel.pregnant}
              isAvailableAiAnswer={client.isWebView}
            />

            <QnaWriteImages
              isUploadingImage={viewModel.isUploadingImage}
              questionImages={viewModel.questionImages}
              imageInputRef={viewModel.imageInputRef}
              onDeleteImage={viewModel.onDeleteImage}
              onUploadImage={viewModel.onUploadImage}
              onHandleChange={viewModel.onHandleChange}
            />
          </QuickAnswerWriteFormWrapper>
          <QuickAnswerWriteInfoBoxWrapper>
            <InfoBox
              variant={INFO_BOX_VARIANT.DEFAULT}
              title="실시간 의료 상담은 이렇게 운영돼요."
              message={infoBoxMessage}
            />
          </QuickAnswerWriteInfoBoxWrapper>
          <StickyButton>
            <StickyButton.Button
              disabled={viewModel.isSubmitButtonDisabled}
              text={'물어보기'}
              type="submit"
              onClick={viewModel.checkEnableSubmit}
            />
          </StickyButton>
          <CategoryBottomSheet
            isVisible={viewModel.showCategoriesBottomSheet}
            toggleBottomSheet={viewModel.toggleCategoriesBottomSheet}
            onClickCategoryItem={viewModel.onClickCategoriesBottomSheetTabListItem}
            categoryGroup={viewModel.categoryGroup}
            selectedTab={viewModel.selectedTab}
            onClickCategoriesBottomSheetTab={viewModel.onClickCategoriesBottomSheetTab}
            getSelectedTabItemList={viewModel.getSelectedTabItemList}
          />
          <WriteOnBoarding
            isVisible={!viewModel.neverSeeAgainGuide && !viewModel.neverSeeAgain.qnaWriteGuide}
            onCloseGuide={viewModel.onCloseGuide}
          />
          <PolicyAgreementBottomSheet
            isSubmitButtonDisabled={viewModel.isSubmitButtonDisabled}
            viewModel={viewModel.policyAgreementBottomSheetViewModel}
            afterSubmit={viewModel.requestAiAnswer}
          />
        </QuickAnswerWriteTemplateContainer>
      </TemplateLayout.Content>
    </TemplateLayout.Wrapper>
  );
};

export default QnaWriteMobileTemplate;

const QuickAnswerWriteTemplateContainer = styled.form`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
`;

const TopTitleWrapper = styled.div`
  padding: 16px 24px 40px;
  width: 100%;
`;

const TopTitle = styled.div`
  margin: 24px 0 4px;

  ${({ theme }) => `
    ${theme.typography.UI22SB};
    color: ${theme.color.gray700}
    `};
`;

const TopDescription = styled.div`
  ${({ theme }) => `
    ${theme.typography.UI16M};
    color: ${theme.color.gray400}
    `};
`;

const QuickAnswerWriteFormWrapper = styled.div`
  width: 100%;
  padding: 0 20px;
`;

const QuickAnswerWriteInfoBoxWrapper = styled.div`
  width: 100%;
`;
